// A-7b PR 6a — item name normalizer for styling row interpolation.
//
// Input: noisy retailer product titles like "ZW COLLECTION FLORAL SHIRT" or
// "Nobody's Child White Ditsy Floral Puff Sleeve Top - Size 10".
// Output: short, generic-feeling phrase suitable for "{item}" interpolation
// in styling pattern copy ("Wrap cut on this {item} defines the waist...").
//
// Pure function — no I/O. Safe to call from anywhere in the bundle.

const CATEGORY_FALLBACK: Record<string, string> = {
  top: "top",
  tops: "top",
  bottom: "trousers",
  bottoms: "trousers",
  dress: "dress",
  dresses: "dress",
  outerwear: "jacket",
  jumpsuit: "jumpsuit",
  playsuit: "playsuit",
  knitwear: "knit",
  bag: "bag",
  shoes: "shoes",
  accessory: "accessory",
};

// Color tokens we strip from leading positions. Kept basic — extend with
// caution; over-stripping risks losing useful descriptors like "floral".
const COLOR_TOKENS = new Set([
  "black", "white", "ivory", "cream", "beige", "tan", "brown", "chocolate",
  "navy", "blue", "cobalt", "teal", "turquoise", "sky", "indigo",
  "red", "burgundy", "wine", "maroon", "scarlet",
  "pink", "blush", "rose", "fuchsia", "magenta",
  "green", "olive", "khaki", "sage", "emerald", "mint", "forest",
  "yellow", "mustard", "gold", "amber",
  "orange", "rust", "coral", "peach",
  "purple", "lilac", "lavender", "plum", "violet",
  "grey", "gray", "charcoal", "silver",
  "multi", "multicolour", "multicolor", "neutral", "nude",
]);

const FABRIC_TOKENS = new Set([
  "cotton", "linen", "silk", "satin", "denim", "leather", "wool", "cashmere",
  "polyester", "viscose", "rayon", "nylon", "modal", "tencel", "lyocell",
  "velvet", "corduroy", "tweed", "jersey", "knit", "knitted", "ribbed",
  "chiffon", "lace", "mesh", "suede", "faux", "blend",
]);

// Adjectives that overlap with the styling row's structural language. Stripping
// these avoids the row reading "Fitted cuts on this fitted top..." double-up.
// Also strips generic print / texture descriptors that add noise without
// helping the styling line read naturally.
const STRUCTURAL_TOKENS = new Set([
  "fitted", "slim", "oversized", "relaxed", "loose", "tight", "tailored",
  "boxy", "cropped", "longline", "structured", "draped",
  "sequin", "sequinned", "sequined", "floral", "ditsy", "printed", "print",
  "striped", "stripe", "checked", "check", "embroidered", "plain", "ribbed",
]);


// Common retailer brand prefixes. Not exhaustive — covers the high-traffic
// noise we see in detected_items.name + garment_data.product_name.
const RETAILER_PREFIXES = [
  "zw collection", "zara", "mango", "h&m", "hm", "asos", "asos design",
  "cos", "m&s", "marks & spencer", "marks and spencer", "next",
  "river island", "new look", "john lewis", "& other stories",
  "stories", "nobody's child", "nobodys child", "& daughter",
  "uniqlo", "arket", "weekday", "monki", "topshop",
  "pretty little thing", "prettylittlething", "plt",
  "boohoo", "missguided", "shein", "manière de voir", "maniere de voir",
  "me+em", "me em", "holland cooper", "anthropologie",
];

function stripRetailerPrefix(s: string): string {
  for (const prefix of RETAILER_PREFIXES) {
    if (s.startsWith(prefix + " ")) return s.slice(prefix.length + 1).trim();
    if (s.startsWith(prefix + ":")) return s.slice(prefix.length + 1).trim();
  }
  return s;
}

function stripTrailingVariant(s: string): string {
  // " - Size 10", " (Black)", " | Size XL", " — Size M"
  return s
    .replace(/\s*[-–—|]\s*size\s+[a-z0-9/]+\s*$/i, "")
    .replace(/\s*\([^)]*\)\s*$/g, "")
    .replace(/\s*-\s*[a-z]+\s*$/i, (m) => {
      const last = m.replace(/^\s*-\s*/i, "").trim().toLowerCase();
      return COLOR_TOKENS.has(last) || FABRIC_TOKENS.has(last) ? "" : m;
    })
    .trim();
}

function stripLeadingTokens(tokens: string[], skip: Set<string>): string[] {
  let i = 0;
  while (i < tokens.length && skip.has(tokens[i])) i += 1;
  return tokens.slice(i);
}

const MAX_LEN = 35;

export function normalizeItemName(
  name: string | null | undefined,
  category: string | null,
): string {
  const catKey = (category ?? "").toLowerCase().trim();
  const fallback = CATEGORY_FALLBACK[catKey] ?? "{item}";

  if (name == null) return fallback;
  let s = String(name).trim().toLowerCase();
  if (!s) return fallback;

  s = stripRetailerPrefix(s);
  s = stripTrailingVariant(s);
  s = s.replace(/[,.;:!?"']/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return fallback;

  // Strip leading color / fabric / structural tokens (in that order; multiple passes).
  let tokens = s.split(" ").filter(Boolean);
  for (let pass = 0; pass < 3; pass += 1) {
    const before = tokens.length;
    tokens = stripLeadingTokens(tokens, COLOR_TOKENS);
    tokens = stripLeadingTokens(tokens, FABRIC_TOKENS);
    tokens = stripLeadingTokens(tokens, STRUCTURAL_TOKENS);
    if (tokens.length === before) break;
  }

  if (tokens.length === 0) return fallback;

  // Keep last 3–5 tokens for a compact phrase. MAX_LEN caps char overflow.
  // (Raised from 4 → 5 in PR 6c-1: prevented "halter" being dropped from
  //  "yellow halter neck draped maxi dress".)
  const kept = tokens.slice(Math.max(0, tokens.length - 5));
  let result = kept.join(" ").trim();
  if (result.length < 2) return fallback;

  if (result.length > MAX_LEN) {
    result = result.slice(-MAX_LEN).replace(/^\.{0,3}\s*/, "").trim();
  }

  return result || fallback;
}
